import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress
} from '@mui/material';
import PasswordInput from '../components/PasswordInput';
import { validatePasswordStrength } from '../utils/passwordValidation';
import PasswordRulesTooltip from '../components/PasswordRulesTooltip';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  
  const { token } = useParams();
  const navigate = useNavigate();

  // Validate the token when the component mounts
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`/api/auth/validate-reset-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
        } else {
          setError('Invalid or expired password reset link');
        }
      } catch (err) {
        setError('Invalid or expired password reset link');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [token]);

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    const { errors } = validatePasswordStrength(value);
    setPasswordErrors(errors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!password || !confirmPassword) {
      return setError('Please fill in all fields');
    }
    
    const { isValid, errors } = validatePasswordStrength(password);
    if (!isValid) {
      setPasswordErrors(errors);
      return setError('Password does not meet security requirements');
    }
    
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    try {
      setLoading(true);
      await axios.post(`/api/auth/reset-password/${token}`, { password });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
      // Remove logic that expects password_errors from backend
      // if (err.response?.data?.password_errors) {
      //   setPasswordErrors(err.response.data.password_errors);
      // }
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Reset Password
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Your password has been reset successfully. You can now log in with your email and password. You will be redirected to the login page in a few seconds.
              </Alert>
            </Box>
          ) : tokenValid ? (
            <Box component="form" onSubmit={handleSubmit}>
              <PasswordInput
                margin="normal"
                required
                fullWidth
                name="password"
                label={<span>New Password <PasswordRulesTooltip placement="right" /></span>}
                id="password"
                value={password}
                onChange={handlePasswordChange}
                error={passwordErrors.length > 0}
                helperText={passwordErrors.length > 0 ? passwordErrors[0] : ''}
                showStrengthIndicator={true}
                autoFocus
              />
              <PasswordInput
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm New Password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                showStrengthIndicator={false}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                The password reset link is invalid or has expired.
              </Typography>
              <Button
                component={Link}
                to="/forgot-password"
                variant="outlined"
              >
                Request a new link
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPassword; 